import type {
    SearchFilterField,
    SearchSchema,
    SearchScope,
    SortDir,
} from '../api/searchTypes';
import type { TagState } from '../components/TagChip';

export const DEFAULTS: Record<string, string> = {
    scope: 'event',
    status: 'open',
    tag_match: 'any',
    neg_risk: 'off',
    sort: 'volume',
    dir: 'desc',
    market_sort: 'default',
    market_dir: 'desc',
};

export const CONTROL_KEYS = [
    'scope',
    'sort',
    'dir',
    'market_sort',
    'market_dir',
];

// The trader view has its own defaults (spec 4.5). `scope` keeps the event
// default so that `scope=trader` stays in the URL.
export function traderDefaults(schema: SearchSchema): Record<string, string> {
    return { scope: DEFAULTS.scope, ...(schema.trader_defaults ?? {}) };
}

export function defaultsFor(
    scope: SearchScope,
    schema: SearchSchema
): Record<string, string> {
    return scope === 'trader' ? traderDefaults(schema) : DEFAULTS;
}

// One merged order for every scope. The scopes use different section names
// (events and markets: topic, structure, dates, price; traders: activity,
// kind, performance, categories); only "text" (the query, never shown as a
// section) and "size" are shared, so the merged order is each scope's order.
export const SECTION_ORDER = [
    'text',
    'activity',
    'kind',
    'topic',
    'structure',
    'size',
    'dates',
    'price',
    'performance',
    'categories',
];

export const SECTION_TITLES: Record<string, string> = {
    text: 'Search',
    topic: 'Category and tags',
    structure: 'Series and structure',
    size: 'Size',
    dates: 'Dates',
    price: 'Price',
    activity: 'Activity',
    kind: 'Kind',
    performance: 'Performance',
    categories: 'Categories',
};

export const FIELD_LABELS: Record<string, string> = {
    q: 'Query',
    status: 'Status',
    event_include: 'Categories',
    event_exclude: 'Categories dropped',
    tags_include: 'Tags',
    tags_exclude: 'Tags dropped',
    tag_match: 'Tag match',
    series: 'Series',
    recurrence: 'Repeats',
    neg_risk: 'Neg risk',
    min_volume: 'Min volume',
    max_volume: 'Max volume',
    min_liquidity: 'Min liquidity',
    start_after: 'Started after',
    start_before: 'Started before',
    closed_after: 'Closed after',
    closed_before: 'Closed before',
    resolved_after: 'Resolved after',
    resolved_before: 'Resolved before',
    created_after: 'Created after',
    created_before: 'Created before',
    min_markets: 'Min markets',
    max_markets: 'Max markets',
    min_open_markets: 'Min open markets',
    min_yes_prob: 'Min price',
    max_yes_prob: 'Max price',
    min_abs_change_1d: 'Min 1d move',
};

export const SORT_LABELS: Record<string, string> = {
    relevance: 'Relevance',
    volume: 'Volume',
    liquidity: 'Liquidity',
    start_date: 'Start date',
    closed_date: 'Closed date',
    resolved_date: 'Resolved date',
    created_at: 'Created',
    end_date: 'Planned end',
    market_count: 'Markets',
    open_market_count: 'Open markets',
    yes_prob: 'Price',
    change_1d: '1d move',
    change_1w: '1w move',
    change_1m: '1m move',
    default: 'Best match',
    question: 'Question',
};

// Trader keys reuse some event names (tags_include, min_volume) with another
// meaning, so the trader view has its own label map.
export const TRADER_FIELD_LABELS: Record<string, string> = {
    q: 'Address or @name',
    active_within_days: 'Active within',
    min_trades: 'Min trades',
    first_seen_after: 'First seen after',
    min_active_days: 'Min active days',
    tags_include: 'Labels',
    tags_exclude: 'Labels dropped',
    tag_match: 'Label match',
    min_volume: 'Min volume',
    max_volume: 'Max volume',
    min_portfolio_value: 'Min portfolio value',
    min_avg_size: 'Min avg trade size',
    min_pnl: 'Min PnL',
    min_win_rate: 'Min win rate',
    min_closed: 'Min settled positions',
    min_roi: 'Min ROI',
    min_profit_factor: 'Min profit factor',
    main_category_include: 'Main category',
    main_category_exclude: 'Main category dropped',
};

const OPTION_LABELS: Record<string, Record<string, string>> = {
    active_within_days: { '1': '1d', '7': '7d', '30': '30d', '90': '90d' },
};

export function fieldLabel(
    field: SearchFilterField,
    scope: SearchScope = 'event'
): string {
    const labels = scope === 'trader' ? TRADER_FIELD_LABELS : FIELD_LABELS;
    return labels[field.key] ?? field.key.replace(/_/g, ' ');
}

export function optionLabel(field: SearchFilterField, option: string): string {
    return OPTION_LABELS[field.key]?.[option] ?? option;
}

export function sortLabel(key: string): string {
    return SORT_LABELS[key] ?? key.replace(/_/g, ' ');
}

export function readParam(
    sp: URLSearchParams,
    key: string,
    defaults: Record<string, string> = DEFAULTS
): string {
    return sp.get(key) ?? defaults[key] ?? '';
}

export function readList(sp: URLSearchParams, key: string): string[] {
    const raw = sp.get(key);
    if (!raw) return [];
    return raw
        .split(',')
        .map(v => v.trim())
        .filter(Boolean);
}

export function readNumber(sp: URLSearchParams, key: string): number | null {
    const raw = sp.get(key);
    if (raw === null || raw === '') return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
}

export function scopeOf(sp: URLSearchParams): SearchScope {
    const scope = sp.get('scope');
    return scope === 'market' || scope === 'trader' ? scope : 'event';
}

export function dirOf(sp: URLSearchParams, key: string): SortDir {
    return sp.get(key) === 'asc' ? 'asc' : 'desc';
}

export function withParam(
    sp: URLSearchParams,
    key: string,
    value: string | null,
    defaults: Record<string, string> = DEFAULTS
): URLSearchParams {
    const next = new URLSearchParams(sp);
    if (value === null || value === '' || value === defaults[key]) {
        next.delete(key);
    } else {
        next.set(key, value);
    }
    if (key !== 'cursor') next.delete('cursor');
    return next;
}

export function withList(
    sp: URLSearchParams,
    key: string,
    values: string[],
    defaults: Record<string, string> = DEFAULTS
): URLSearchParams {
    return withParam(
        sp,
        key,
        values.length ? values.join(',') : null,
        defaults
    );
}

export function toggleInList(
    sp: URLSearchParams,
    key: string,
    value: string,
    defaults: Record<string, string> = DEFAULTS
): URLSearchParams {
    const current = readList(sp, key);
    const next = current.includes(value)
        ? current.filter(v => v !== value)
        : [...current, value];
    return withList(sp, key, next, defaults);
}

// Include and exclude lists of one kind (tags_include / tags_exclude,
// event_include / event_exclude, ...): a value sits in at most one of them.
export function listState(
    sp: URLSearchParams,
    includeKey: string,
    excludeKey: string,
    value: string
): TagState {
    if (readList(sp, includeKey).includes(value)) return 'include';
    if (readList(sp, excludeKey).includes(value)) return 'exclude';
    return 'off';
}

// Toggles the value in the target list and takes it out of the other one.
export function exclusiveToggle(
    sp: URLSearchParams,
    includeKey: string,
    excludeKey: string,
    value: string,
    target: 'include' | 'exclude',
    defaults: Record<string, string> = DEFAULTS
): URLSearchParams {
    const self = target === 'include' ? includeKey : excludeKey;
    const other = target === 'include' ? excludeKey : includeKey;
    const next = toggleInList(sp, self, value, defaults);
    return withList(
        next,
        other,
        readList(next, other).filter(v => v !== value),
        defaults
    );
}

export function buildApiQuery(
    sp: URLSearchParams,
    spec: SearchFilterField[],
    limit: number,
    cursor: string | null
): string {
    const allowed = new Set([...spec.map(f => f.key), ...CONTROL_KEYS]);
    const out = new URLSearchParams();
    for (const [key, value] of sp.entries()) {
        if (!allowed.has(key)) continue;
        if (value === '' || value === DEFAULTS[key]) continue;
        out.set(key, value);
    }
    out.set('scope', scopeOf(sp));
    out.set('limit', String(limit));
    if (cursor) out.set('cursor', cursor);
    return out.toString();
}

export function activeFilterCount(
    sp: URLSearchParams,
    spec: SearchFilterField[],
    defaults: Record<string, string> = DEFAULTS
): number {
    let count = 0;
    for (const field of spec) {
        if (field.key === 'q') continue;
        const value = sp.get(field.key);
        if (value && value !== defaults[field.key]) count++;
    }
    return count;
}

export function resetFilters(
    sp: URLSearchParams,
    spec: SearchFilterField[]
): URLSearchParams {
    const next = new URLSearchParams(sp);
    for (const field of spec) {
        if (field.key === 'q') continue;
        next.delete(field.key);
    }
    next.delete('cursor');
    return next;
}
